import * as Sentry from "@sentry/react";
import { z } from "zod";
import { useApiQuery, type Api } from "../../../services/api";
import { memoryStore, useStore } from "../../../lib/store";
import { useActiveLang } from "../../app/hooks/useActiveLang";
import {
  useGetRoomSectionsQuery,
  type RoomEntry,
  type ResolvedRoomSection,
  type RoomSectionItem,
} from "./useGetRoomSectionsQuery";

/**
 * A room's Resources are the union of two admin gestures that write to two
 * different places and are never linked:
 *
 * - curation — `POST /admin/rooms/:roomId/sections/:sectionId/items` appends to
 *   the section's JSONB `items`, surfaced by `/webapp/public/rooms/:id/sections`;
 * - membership — `POST /admin/rooms/:id/content` inserts a `content_rooms` row
 *   (Video/Infographic), and podcasts linked to the room.
 *
 * Neither is authoritative alone: on production some rooms are described only by
 * sections, some only by membership, most by both. The server merges them into
 * the "All" list (`/webapp/public/rooms/:roomId/resources`); a section's own
 * view reads that section's items.
 *
 * Room member *articles* are deliberately excluded — only sectioned articles show
 * here. Listing unsectioned articles flooded Resources with the Discovery feed
 * (reverted in 9871cbcd41); they stay reachable from the Discovery tab.
 */
export type RoomResourceListEntry =
  Api["get"]["/webapp/public/rooms/:roomId/resources"]["output"]["body"]["items"][number];

/** A card of the list: every entry but a congress space (a block). */
export type RoomResourceListItem = Exclude<
  RoomResourceListEntry,
  { kind: "congress" }
>;

/**
 * The kinds this bundle can show. Read at the boundary: a newer API can send a
 * kind this bundle does not know, and the entry is then left out, with a
 * warning, rather than crash the room page on an exhaustive switch.
 */
const knownKind = z.object({
  kind: z.enum(["article", "content", "playlist", "podcast", "congress"]),
});

function knownEntries(
  roomId: string,
  entries: readonly RoomResourceListEntry[]
): RoomResourceListEntry[] {
  return entries.filter((entry) => {
    if (knownKind.safeParse(entry).success) return true;
    Sentry.captureMessage("room resources: an entry of unknown kind", {
      level: "warning",
      tags: { roomId },
      extra: { roomId, entry },
    });
    return false;
  });
}

/**
 * The cards of a list that places no congress space: the desktop's
 * (`include`), whose spaces show on their chips only.
 */
export function cardsOf(
  entries: readonly RoomResourceListEntry[]
): RoomResourceListItem[] {
  return entries.map((entry) => {
    if (entry.kind === "congress")
      throw new Error(
        "should not happen: an `include` list places no congress space"
      );
    return entry;
  });
}

function timeOf(value: string): number {
  return new Date(value).getTime();
}

/**
 * Curated section items → list items. Exported so a section-filtered view builds
 * its list through the same mapping the merged list uses.
 */
export function toResourceListItems(
  sectionItems: readonly RoomSectionItem[]
): RoomResourceListItem[] {
  return sectionItems.map((item): RoomResourceListItem => {
    switch (item.type) {
      case "article":
        return {
          kind: "article",
          sortDate: timeOf(
            item.article.publication_date === null
              ? item.addedAt
              : item.article.publication_date
          ),
          article: item.article,
        };
      case "content":
        return {
          kind: "content",
          sortDate: timeOf(
            item.content.publication_date === null
              ? item.addedAt
              : item.content.publication_date
          ),
          content: item.content,
          // A section view shows what CX put in it: no item stands in for a
          // series there.
          episodeOf: null,
        };
      case "playlist":
        return {
          kind: "playlist",
          sortDate: timeOf(
            item.playlist.publicationDate === null
              ? item.addedAt
              : item.playlist.publicationDate
          ),
          playlist: item.playlist,
        };
      default:
        throw new Error(item satisfies never);
    }
  });
}

/**
 * How many items of "All" a page shows at first: more than the largest list
 * measured (36 items on staging, 2026-10-22), so a room shows its whole list
 * at once, as it did when the first pages of its sources loaded together.
 */
const FIRST_SHOWN = 40;

/** How many more each "Show more", or scroll to the end, adds. */
const SHOWN_STEP = 20;

/**
 * How much of each "All" list the reader has opened, by room, language and
 * mode. Kept across a navigation away and back, as the loaded pages of a paged
 * query were, so a return restores the list and its scroll position.
 */
const shownCounts = memoryStore<ReadonlyMap<string, number>>(new Map());

type Result =
  | { state: "pending" }
  | { state: "error"; message: string }
  | {
      state: "success";
      /** The room's Resources order: sections and congress spaces. */
      entries: RoomEntry[];
      sections: ResolvedRoomSection[];
      /**
       * The first entries of "All", as the server ordered it: the cards and,
       * on the phone, the congress spaces at their places.
       */
      items: RoomResourceListEntry[];
      /** Every congress space the whole list places, shown or not yet. */
      placedCongressSpaceIds: string[];
      canShowMore: boolean;
      showMore: () => void;
    };

/**
 * The room's Resources: its entries (the chips) and the "All" list.
 *
 * `congressDocuments`: the phone shows the congress spaces in blocks above
 * "All", so it asks the server to leave their documents out ("exclude"); the
 * desktop shows a space only on its chip, so they stay ("include").
 */
export function useGetRoomResourceItemsQuery(
  roomId: string | null,
  congressDocuments: "exclude" | "include"
): Result {
  const { contentLang } = useActiveLang();
  const [counts, setCounts] = useStore(shownCounts);

  const sectionsQuery = useGetRoomSectionsQuery(
    roomId === null ? "" : roomId,
    contentLang,
    roomId !== null
  );
  const allQuery = useApiQuery(
    "/webapp/public/rooms/:roomId/resources",
    roomId === null
      ? false
      : {
          param: { roomId },
          query: { language: contentLang, congressDocuments },
        }
  );

  if (sectionsQuery.state === "error") {
    return { state: "error", message: sectionsQuery.message };
  }
  if (allQuery.state === "error") {
    return { state: "error", message: allQuery.message };
  }
  if (sectionsQuery.state !== "success" || allQuery.state !== "success") {
    return { state: "pending" };
  }

  const listKey = `${roomId}:${contentLang}:${congressDocuments}`;
  const counted = counts.get(listKey);
  const shown = counted === undefined ? FIRST_SHOWN : counted;
  if (roomId === null)
    throw new Error("should not happen: no query runs without a room");
  const items = knownEntries(roomId, allQuery.body.items);
  // `shown` counts the cards: a congress block was never counted, when the
  // blocks sat above the list.
  const end = endOfShown(items, shown);
  return {
    state: "success",
    entries: sectionsQuery.entries,
    sections: sectionsQuery.sections,
    items: items.slice(0, end),
    placedCongressSpaceIds: items.flatMap((entry) =>
      entry.kind === "congress" ? [entry.spaceId] : []
    ),
    canShowMore: items.length > end,
    showMore: () => {
      setCounts((previous) =>
        new Map(previous).set(listKey, shown + SHOWN_STEP)
      );
    },
  };
}

/**
 * Where the first `shown` cards end in `entries`: the congress blocks among
 * them, and those that follow the last card shown, come along.
 */
function endOfShown(
  entries: readonly RoomResourceListEntry[],
  shown: number
): number {
  let cards = 0;
  for (const [index, entry] of entries.entries()) {
    if (entry.kind === "congress") continue;
    if (cards === shown) return index;
    cards += 1;
  }
  return entries.length;
}
